import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  NgZone,
  OnInit,
  output,
  Renderer2,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { produce } from 'immer';
import {
  animationFrameScheduler,
  filter,
  finalize,
  fromEvent,
  map,
  Observable,
  switchMap,
  takeLast,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs';

import {
  makeColumnGridStyle,
  PropertiesConfigInputTableColumnModel,
} from '@blaze-shared/elements-domain';
import { findColumnIndex } from '@blaze-shared/schema-domain';
import { isDefined, mouseRxUtils, runAfterFramePaint } from '@blaze-shared/shared-util';

import { TableVirtualScrollFacade } from '../virtual-scroll';

interface ColumnData {
  id: `colId:${string}_${string}`;
  name: string;
  element?: HTMLElement;
  widthStyle: string;
  widthCalculated: number;
}

const MIN_COLUMN_WIDTH_PX = 32;
const RESIZING_CLASSNAME = 'resizing';

interface ResizeConfig {
  startX: number;
  columnWidth: number;
  visibleIndex: number;
  previousSiblings: ColumnData[];
  nextSiblings: ColumnData[];
  isLastColumn: boolean;
}

@UntilDestroy()
@Component({
  selector: 'be-resize-column',
  template: '',
  styleUrls: ['./resize-column.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResizeColumnComponent implements OnInit {
  private readonly renderer = inject(Renderer2);
  private readonly el = inject(ElementRef);
  private readonly tableVirtualScrollFacade = inject(TableVirtualScrollFacade);
  private readonly ngZone = inject(NgZone);

  columnName = input.required<string>();
  cell = input.required<HTMLElement>();
  row = input.required<HTMLElement>();
  tableWidth = input.required<number>();
  /** All columns exactly as they appear in the model in props */
  allColumns = input.required<PropertiesConfigInputTableColumnModel[]>();

  /** Runtime column names */
  displayedColumns = input.required<string[]>();
  /** Definitions updated with the latest column names at runtime */
  displayedColumnsDef = input.required<PropertiesConfigInputTableColumnModel[]>();
  disableResize = input<boolean>();
  cellSelector = input<string>('[data-col-name]');

  resizeColumn = output<PropertiesConfigInputTableColumnModel[]>();

  private readonly resizer: HTMLElement;
  private pendingLiveWidths: { styles: string[]; columnWidths: number[] } | null = null;
  private liveWidthsAnimationFrame: number | null = null;

  cells = computed(() => Array.from(this.row().querySelectorAll<HTMLElement>(this.cellSelector())));
  displayedColumnsCount = computed(() => this.displayedColumns().length);
  displayedColumnsDefCount = computed(() => this.displayedColumnsDef().length);

  constructor() {
    this.resizer = this.el.nativeElement;
  }

  ngOnInit(): void {
    this.setupColumnResizing();
  }

  private setupColumnResizing(): void {
    fromEvent<MouseEvent>(this.resizer, 'mousedown')
      .pipe(
        tap((event) => this.handleMousedownStart(event)),
        map((event) => this.mapToInitialResizeConfig(event)),
        filter(({ visibleIndex }) => visibleIndex >= 0),
        map((startConfig) => this.mapToEnhancedResizeConfig(startConfig)),
        throttleTime(0, animationFrameScheduler),
        switchMap((config) => this.createMousemoveStream(config)),
        untilDestroyed(this),
      )
      .subscribe((data) => this.handleResizeCommit(data));
  }

  private handleMousedownStart(event: MouseEvent): void {
    event.preventDefault();
    this.renderer.addClass(this.resizer, RESIZING_CLASSNAME);
  }

  private mapToInitialResizeConfig(
    event: MouseEvent,
  ): Pick<ResizeConfig, 'startX' | 'columnWidth' | 'visibleIndex'> {
    return {
      startX: event.pageX,
      columnWidth: this.cell()?.offsetWidth ?? MIN_COLUMN_WIDTH_PX,
      visibleIndex: this.displayedColumns().indexOf(this.columnName()),
    };
  }

  private mapToEnhancedResizeConfig(
    start: Pick<ResizeConfig, 'startX' | 'columnWidth' | 'visibleIndex'>,
  ): Omit<ResizeConfig, keyof Pick<ResizeConfig, 'startX' | 'columnWidth' | 'visibleIndex'>> &
    Pick<ResizeConfig, 'startX' | 'columnWidth' | 'visibleIndex'> {
    const displayedColumns = this.displayedColumns();
    const displayedColumnsDef = this.displayedColumnsDef() ?? [];
    const columnIndex = start.visibleIndex;

    const colsData = this.buildColumnsData(displayedColumns, displayedColumnsDef);

    const previousSiblings = colsData.slice(0, columnIndex + 1);
    const nextSiblings = colsData.slice(columnIndex + 1);
    const isLastColumn = columnIndex === displayedColumns.length - 1;

    return { ...start, previousSiblings, nextSiblings, isLastColumn };
  }

  private buildColumnsData(displayedColumns: string[], displayedColumnsDef: any[]): ColumnData[] {
    return displayedColumns.map((name, _, collection) => {
      const colDef = displayedColumnsDef.find((def) => def.name === name);
      const colEl = this.cells().find((el) => el.dataset.colName === name);

      if (!colEl) {
        console.error(`Column [data-col-name="${name}"] was not found`);
      }

      return {
        id: colDef?.id,
        name: colDef?.name,
        element: colEl,
        widthStyle: makeColumnGridStyle(name, collection.length, colDef),
        widthCalculated: colEl?.offsetWidth ?? MIN_COLUMN_WIDTH_PX,
      };
    });
  }

  private createMousemoveStream(
    config: ResizeConfig,
  ): Observable<{ affectedColumns: ColumnData[] }> {
    return fromEvent<MouseEvent>(document, 'mousemove').pipe(
      map((moveEvent) => this.calculateNewWidths(config, moveEvent)),
      takeUntil(mouseRxUtils.buttonRelease()),
      takeLast(1),
      finalize(() => this.handleDragCleanup()),
    );
  }

  private calculateNewWidths(
    config: ResizeConfig,
    moveEvent: MouseEvent,
  ): { affectedColumns: ColumnData[] } {
    const deltaX = moveEvent.pageX - config.startX;
    let newWidth = config.columnWidth + deltaX;
    let updateAllColumns = false;

    newWidth = Math.max(newWidth, MIN_COLUMN_WIDTH_PX);

    if (config.isLastColumn) {
      newWidth = this.constrainLastColumnWidth(config, newWidth);
    } else if (this.shouldDistributeWidthToLastSibling(config)) {
      const totalCurrentWidth = this.calculateTotalCurrentWidth(config, newWidth);

      if (totalCurrentWidth <= this.tableWidth()) {
        const lastNextSibling = config.nextSiblings.at(-1);

        if (lastNextSibling) {
          lastNextSibling.widthCalculated =
            (lastNextSibling.widthCalculated ?? 0) + this.tableWidth() - totalCurrentWidth;
          updateAllColumns = true;
        }
      }
    }

    const resizedColumn = config.previousSiblings.at(-1);
    if (resizedColumn) {
      resizedColumn.widthCalculated = newWidth;
    }

    this.updateTableStyles(config, updateAllColumns);
    return { affectedColumns: config.previousSiblings };
  }

  private constrainLastColumnWidth(config: ResizeConfig, width: number): number {
    const totalPreviousWidth = config.previousSiblings
      .slice(0, -1)
      .reduce((sum, col) => col.widthCalculated + sum, 0);

    const newTotalWidth = totalPreviousWidth + width;

    if (newTotalWidth < this.tableWidth()) {
      return this.tableWidth() - totalPreviousWidth;
    }

    return width;
  }

  private shouldDistributeWidthToLastSibling(config: ResizeConfig): boolean {
    return (
      config.nextSiblings.every((col) => col.widthCalculated !== undefined) &&
      this.displayedColumnsDefCount() === this.displayedColumnsCount()
    );
  }

  private calculateTotalCurrentWidth(config: ResizeConfig, newWidth: number): number {
    const prevSiblingsSum = config.previousSiblings
      .slice(0, -1)
      .reduce((sum, col) => col.widthCalculated + sum, 0);

    const nextSiblingsSum = config.nextSiblings.reduce((sum, col) => col.widthCalculated + sum, 0);

    return prevSiblingsSum + nextSiblingsSum + newWidth;
  }

  private handleDragCleanup(): void {
    this.renderer.removeClass(this.resizer, RESIZING_CLASSNAME);

    if (this.liveWidthsAnimationFrame !== null) {
      window.cancelAnimationFrame(this.liveWidthsAnimationFrame);
      this.liveWidthsAnimationFrame = null;
      this.pendingLiveWidths = null;
    }

    this.ngZone.runOutsideAngular(() => {
      runAfterFramePaint(() => {
        this.tableVirtualScrollFacade.commitColumnResize();
      });
    });
  }

  private handleResizeCommit({ affectedColumns }: { affectedColumns: ColumnData[] }): void {
    const allColumns = this.allColumns();
    if (!allColumns) {
      return;
    }

    const updatedColumnsData = produce(allColumns, (draft) => {
      for (const column of affectedColumns) {
        const index = findColumnIndex(allColumns, column);
        if (index !== undefined && index >= 0) {
          draft[index].width = column.widthCalculated;
        }
      }
    });

    this.resizeColumn.emit(updatedColumnsData);
  }

  private updateTableStyles(
    config: {
      previousSiblings: Partial<ColumnData>[];
      nextSiblings: Partial<ColumnData>[];
    },
    updateAllColumnsWidths: boolean,
  ): void {
    const styles = [
      ...config.previousSiblings.map((col) => `${col.widthCalculated}px`),
      ...config.nextSiblings.map((col) => col.widthStyle),
    ] as string[];

    const columnWidths = config.previousSiblings.map((col) => col.widthCalculated);

    if (updateAllColumnsWidths) {
      columnWidths.push(...config.nextSiblings.map((col) => col.widthCalculated));
    }

    this.pendingLiveWidths = { styles, columnWidths: columnWidths.filter(isDefined) };
    this.liveWidthsAnimationFrame ??= window.requestAnimationFrame(() => {
      this.liveWidthsAnimationFrame = null;
      const pending = this.pendingLiveWidths;
      this.pendingLiveWidths = null;
      if (pending) {
        this.tableVirtualScrollFacade.applyLiveColumnWidths(pending.styles, pending.columnWidths);
      }
    });
  }
}
